import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { User as OidcUser, UserManager, UserManagerSettings } from 'oidc-client';
import { BehaviorSubject, filter, from, Observable, of, Subscription } from 'rxjs';
import jwt_decode from 'jwt-decode';
import { User } from '../../shared/model/user';
import { Brand as AuthBrand } from 'app/brand-config';
import { MsalFactoryService } from './msal-factory.service';
import { EventType, EventMessage, RedirectRequest, AccountInfo } from '@azure/msal-browser';
import { BrandService } from 'app/app-services/brand.service';

export enum Roles {
  ADMIN = "Admin",
  SYSTEMOWNER = "System Owner",
  READER = "Reader",
  COLLABORATOR = "Collaborator",
  DCODEUSER = "DcodeUser",
  ADVANCED = "Advanced"
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  stringifiedData: any;
  userSubject: BehaviorSubject<User> = new BehaviorSubject(null);
  private userManager: UserManager;
  private oidcUser: OidcUser;
  private userRoles: string[];
  jwtToken: string;
  private msalFactory = inject(MsalFactoryService);
  private _currentAuthType: AuthBrand = this.getStoredAuthType();
  private _isInitialized = false;

  constructor(private router: Router, private brandService: BrandService) {
    this.initialize().catch(err =>
      console.error('MSAL initialize failed', err)
    );
    this.subscribeToMsalEvents();
    // Populate user from existing MSAL account if already logged in (e.g., on page refresh)
    this.loadUserFromActiveAccount();
  }

  get currentAuthType(): AuthBrand {
    return this._currentAuthType;
  }

  public get currentMsalService() {
    return this.msalFactory.getMsalService(this._currentAuthType);
  }

  handleRedirectCallback(): Subscription {
    // 0. Only run if URL actually looks like a redirect from Entra ID
    const hash = window.location.hash || '';
    const search = window.location.search || '';

    const hasAuthResponse =
      hash.includes('code=') ||
      hash.includes('id_token=') ||
      search.includes('code=') ||
      search.includes('id_token=');

    if (!hasAuthResponse) {
      // Normal page load → nothing for MSAL to process
      this.brandService.setAvailableBrandsFromAuthBrand(this.currentAuthType);
      return;
    }

    return this.currentMsalService
      .handleRedirectObservable()
      .subscribe({
        next: (result) => {
          if (result) {
            this.currentMsalService.instance.setActiveAccount(result.account);
            // Set the user from the MSAL account info
            this.setUser(result.account);

            // Set available brands based on auth type FIRST
            this.brandService.setAvailableBrandsFromAuthBrand(this.currentAuthType);

            // Set the selected brand WITHOUT triggering the redirect
            // Use a method on BrandService that doesn't cause reload
            this.brandService.setSelectedBrandWithoutRedirect(this.currentAuthType);

            localStorage.setItem('access_token', result.accessToken);
            this.router.navigate(['/content/specifications']);
          }
        },
        error: (error: any) => {
          // Ignore the “no token in cache / state not found” noise,
          // log everything else
          const code = (error && error.errorCode) || '';
          if (
            code === 'no_token_request_cache_error' ||
            code === 'state_not_found'
          ) {
            // harmless in this context
            return;
          }

          console.error('Redirect Callback Error', error);
        },
      });
  }

  private getStoredAuthType(): AuthBrand {
    try {
      const stored = localStorage.getItem('authType');
      return (stored as AuthBrand) || 'Scania';
    } catch (e) {
      console.warn('Cannot access localStorage, using default AuthType');
      return 'Scania';
    }
  }

  private persistAuthType(type: AuthBrand) {
    try {
      localStorage.setItem('authType', type);
    } catch (e) {
      console.warn('Cannot persist authType', e);
    }
  }

  async initialize(): Promise<void> {
    if (this._isInitialized) {
      return;
    }
    const msal = this.currentMsalService.instance;
    try {
      await msal.initialize?.(); // optional if using v3+
      this._isInitialized = true;
    } catch (e) {
      console.error('MSAL initialize error', e);
      this._isInitialized = false;
      throw e;
    }
  }

  async setAuthType(type: AuthBrand): Promise<void> {
    if (this._currentAuthType === type && this._isInitialized) {
      return;
    }
    this._currentAuthType = type;
    this.persistAuthType(type);
    this._isInitialized = false;
    await this.initialize();
    console.log(`Auth type switched to ${type} and initialized`);
  }

  isLoggedIn(): boolean {
    try {
      const accounts = this.currentMsalService.instance.getAllAccounts();
      return accounts.length > 0;
    } catch (e) {
      console.error('Error checking login status', e);
      return false;
    }
  }

  getAccessToken(): Observable<string | null> {
    return from(this.ensureInitialized().then(() => {
      const msalService = this.currentMsalService;
      const accounts = msalService.instance.getAllAccounts();
      if (!accounts || accounts.length === 0) return null;
      const account = msalService.instance.getActiveAccount() || accounts[0];
      const scopes = this.getScopes();
      return msalService.instance.acquireTokenSilent({
        account,
        scopes
      }).then(result => {
        localStorage.setItem('access_token', result.accessToken);
        return result.accessToken;
      }).catch(() => {
        return null;
      });
    }));
  }

  refreshToken(): Observable<string> {
    return from(this.ensureInitialized().then(async () => {
      const msalService = this.currentMsalService;
      const accounts = msalService.instance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        this.removeLocalLoginState();
        throw new Error('No account');
      }
      const account = msalService.instance.getActiveAccount() || accounts[0];
      const scopes = this.getScopes();
      try {
        const result = await msalService.instance.acquireTokenSilent({
          account,
          scopes,
          forceRefresh: true
        });
        localStorage.setItem('access_token', result.accessToken);
        return result.accessToken;
      } catch (e) {
        await this.logout();
        throw e;
      }
    }));
  }

  private async ensureInitialized(): Promise<void> {
    if (!this._isInitialized) {
      await this.initialize();
    }
  }

  getCurrentUserRoles(): string[] {
    const msalService = this.currentMsalService;
    const accounts: AccountInfo[] = msalService.instance.getAllAccounts();
    if (accounts && accounts.length > 0) {
      const roles = accounts[0].idTokenClaims?.['roles'] as string[];
      return roles || [];
    }
    return [];
  }

  private subscribeToMsalEvents(): void {
    const broadcastService = this.msalFactory.getMsalBroadcastService(this._currentAuthType);
    broadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS))
      .subscribe((result) => {
        console.debug('MSAL LOGIN_SUCCESS', result);
        // Set the user when login succeeds
        const account = this.currentMsalService.instance.getActiveAccount();
        if (account) {
          this.setUser(account);
        }
      });

    broadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS))
      .subscribe(() => {
        console.debug('MSAL LOGOUT_SUCCESS');
      });
  }

  private loadUserFromActiveAccount(): void {
    // Check if there's an active MSAL account and populate userSubject
    // This handles page refreshes where the user is already logged in but userSubject hasn't been populated yet
    try {
      const account = this.currentMsalService.instance.getActiveAccount();
      if (account && !this.userSubject.value) {
        // User is already logged in but userSubject is not yet populated
        this.setUser(account);
      }
    } catch (error) {
      console.warn('Error loading user from active account:', error);
    }
  }

  getScopes(): string[] {
    const brand = this._currentAuthType;
    const msalMap = environment.msal as Record<AuthBrand, { scopes?: string[] }>;
    return msalMap[brand]?.scopes ?? [];
  }

  clearMSALInteractionState(): void {
    Object.keys(sessionStorage).forEach(key => {
      if (key.includes('msal.') && key.includes('interaction')) {
        sessionStorage.removeItem(key);
      }
    });
  }

  async loginRedirect(loginRequest: RedirectRequest): Promise<void> {
    // Make sure MSAL for the current brand is initialized
    await this.initialize();
    // Kick off the MSAL redirect
    this.currentMsalService.loginRedirect(loginRequest);
  }

  fullLogout() {
    this.clearStoredAuthType();
    this.logout();
  }

  clearStoredAuthType(): void {
    try {
      localStorage.removeItem('authType');
    } catch (e) {
      console.warn('Cannot clear localStorage', e);
    }
  }

  async logout() {
    if (this.isLoggedIn()) {
      // remove local login state first
      this.removeLocalLoginState();
      // then call msal logout
      await this.logoutMSAL();
    } else {
      console.warn('No user is currently logged in.');
      return;
    }
  }

  private removeLocalLoginState() {
    const authType = localStorage.getItem('authType');
    sessionStorage.clear();
    localStorage.clear();
    if (authType) {
      localStorage.setItem('authType', authType);
    }
  }

  private async logoutMSAL(): Promise<void> {
    await this.initialize();

    await this.currentMsalService.logoutRedirect({
      postLogoutRedirectUri: '/login'
    });

    this.clearMSALInteractionState();
  }

  get user(): Observable<User> {
    return this.userSubject.asObservable();
  }

  public setUser(accountInfo: AccountInfo | OidcUser | any) {
    if (!accountInfo) {
      this.userSubject.next(null);
      return;
    }

    const user = new User();

    // Handle MSAL AccountInfo format
    if (accountInfo.idTokenClaims) {
      const claims = accountInfo.idTokenClaims;

      // Extract name parts
      if (accountInfo.name) {
        const nameParts = accountInfo.name.split(' ');
        user.firstName = nameParts[0] ?? '';
        user.lastName = nameParts.slice(1).join(' ') ?? '';
      }

      user.userName = claims['preferred_username'] ?? accountInfo.username ?? accountInfo.name ?? '';
      user.email = claims['email'] ?? claims['preferred_username'] ?? '';
      user.name = accountInfo.name ?? '';

      // Check for admin role
      const roles = claims['roles'] as string[] ?? [];
      user.isAdmin = roles.includes('admin') || roles.includes('AP3698001-dcode-prod-admin');
    }
    // Handle OIDC format (fallback for legacy code)
    else if (accountInfo.email || accountInfo.given_name) {
      user.userName = accountInfo.email;
      user.firstName = accountInfo.given_name ?? accountInfo.firstName ?? '';
      user.lastName = accountInfo.family_name ?? accountInfo.lastName ?? '';
      user.email = accountInfo.email ?? '';
      user.isAdmin = accountInfo.isAdmin ?? false;
    }

    this.userSubject.next(user);
  }

  public getUser() {
    return of(this.user);
  }

  public getUserRole() {
    return localStorage.getItem("currentUserRole");
  }

  public checkUserReader() {
    return this.getUserRole() == Roles.READER;
  }

  public checkUserCollaborator() {
    return this.getUserRole() == Roles.COLLABORATOR;
  }

  getDecodedAccessToken(token: string): any {
    try {
      return jwt_decode(token);
    } catch (Error) {
      return null;
    }
  }

  private createUser(oidcUser: any): User {
    const user = new User();

    if (oidcUser) {
      // if (oidcUser.profile) {
      user.userName = oidcUser.email;
      if (oidcUser.given_name && oidcUser.family_name) {

        if (oidcUser.givenName && oidcUser.surname) {
          user.firstName = oidcUser.given_name;
          user.lastName = oidcUser.family_name;

        } else {
          user.firstName = oidcUser.name;
          // this.currentUser.firstName = oidcUser.displayName;
        }
        // user.isAdmin = oidcUser.DcodeAdmin !== undefined;
        user.isAdmin = localStorage.getItem("currentUserRole") !== undefined;
      }
    }
    return user;
  }

  //Only Admin
  canEditIdentificationsGroup(): boolean {
    return (this.getUserRole() == Roles.ADMIN)
  }
  canEditDignosticFamilies(): boolean {
    return (this.getUserRole() == Roles.ADMIN)
  }
  canEditUsers(): boolean {
    return (this.getUserRole() == Roles.ADMIN)
  }
  canCreateExportLabel(): boolean {
    return (this.getUserRole() == Roles.ADMIN)
  }
  canManageTemplate(): boolean {
    return (this.getUserRole() == Roles.ADMIN)
  }
  canApproveRbacc(): boolean {
    return (this.getUserRole() == Roles.ADMIN)
  }

  //Only Admin or System Owner
  canReleaseVersion(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER)
  }
  canCreateDeleteSpecification(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER)
  }
  canEditSharedFiles(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER)
  }
  canEditRbacc(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER)
  }
  canEditSCOMMFiles(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER)
  }
  canEditServer(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER)
  }
  canEditDiagnosticService(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER)
  }
  canEditLegacyFile(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER)
  }

  //Only Admin or System Owner or collaborator
  canEditCategoryItems(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER) || (this.getUserRole() == Roles.COLLABORATOR)
  }
  CanEditSpecificationVersion(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER) || (this.getUserRole() == Roles.COLLABORATOR)
  }
  canAddVersions(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER) || (this.getUserRole() == Roles.COLLABORATOR)
  }
  canValidateServerFile(): boolean {
    return (this.getUserRole() == Roles.ADMIN) || (this.getUserRole() == Roles.SYSTEMOWNER) || (this.getUserRole() == Roles.COLLABORATOR)
  }

  //Only Admin or System Owner or collaborator or reader
  canEditExportPackage(): boolean {
    return (this.getUserRole() == Roles.ADMIN || this.getUserRole() == Roles.SYSTEMOWNER || this.getUserRole() == Roles.COLLABORATOR || this.getUserRole() == Roles.READER)
  }
  canExportByLabel(): boolean {
    return (this.getUserRole() == Roles.ADMIN || this.getUserRole() == Roles.SYSTEMOWNER || this.getUserRole() == Roles.COLLABORATOR || this.getUserRole() == Roles.READER)
  }
}
